import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleRatingBadge from 'src/components/simple-rating-badge';

import Link from '@/components/link';
import { useGetContributorsFieldConfig } from '@/utils/table/hooks/useGetContributorsFieldConfig';
import { useGetDepartmentFieldConfig } from '@/utils/table/hooks/useGetDepartmentFieldConfig';
import { useGetOwnersFieldConfig } from '@/utils/table/hooks/useGetOwnersFieldConfig';
import type {
  StatefulTableOptions,
  UseGetTablePropsOptions,
} from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetStatelessTableProps } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import { useGetTagFieldConfig } from '@/utils/table/hooks/useGetTagFieldConfig';
import { exportStyleFromValue } from '@/utils/table/pdfExportStyles';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { assessmentDetailsUrl } from '@/utils/urls';

import type { AssessmentFields, AssessmentRegisterFields } from './types';
import type { AssessmentTypeEnum } from './useAssessmentTypeConfig';
import { useAssessmentTypeConfig } from './useAssessmentTypeConfig';
import { useLabelledFields } from './useLabelledFields';

const useGetFieldConfig = (
  assessmentMode: AssessmentTypeEnum,
  useAbsoluteUrls?: boolean
): TableFields<AssessmentRegisterFields> => {
  const { taxonomyKey, outcomeRatingKey, parentType } =
    useAssessmentTypeConfig(assessmentMode);
  const { t } = useTranslation('common', {
    keyPrefix: `${taxonomyKey}.columns`,
  });
  const allOwners = useGetOwnersFieldConfig<AssessmentRegisterFields>({
    formId: parentType,
    fieldId: 'Owners',
  });
  const allContributors =
    useGetContributorsFieldConfig<AssessmentRegisterFields>({
      formId: parentType,
      fieldId: 'Contributors',
    });
  const { getByValue: statusGetByValue } = useRating('assessment_status');
  const { getByValue: outcomeGetByValue } = useRating(outcomeRatingKey);
  const tagField = useGetTagFieldConfig<AssessmentRegisterFields>({
    formId: parentType,
    fieldId: 'tags',
  });
  const departmentField = useGetDepartmentFieldConfig<AssessmentRegisterFields>(
    (r) => r.departments,
    {
      formId: parentType,
      fieldId: 'departments',
    }
  );
  const { t: gt } = useTranslation(['common'], { keyPrefix: 'columns' });

  return useMemo(
    () => ({
      Title: {
        custom: false,
        formId: parentType,
        fieldId: 'Title',
        cell: (item) => {
          return (
            <Link
              isRelativeUrl={!useAbsoluteUrls}
              variant={'secondary'}
              href={useAbsoluteUrls ? assessmentDetailsUrl(item.Id) : item.Id}
            >
              {item.Title}
            </Link>
          );
        },
        isRowHeader: true,
      },
      StartDate: dateColumnFromConfig({
        header: { formId: parentType, fieldId: 'StartDate' },
        dateField: 'StartDate',
        isDateRangeFilter: true,
      }),
      ActualCompletionDate: dateColumnFromConfig({
        header: { formId: parentType, fieldId: 'ActualCompletionDate' },
        dateField: 'ActualCompletionDate',
        isDateRangeFilter: true,
      }),
      StatusLabelled: {
        custom: false,
        formId: parentType,
        fieldId: 'Status',
        cell: (item) => (
          <SimpleRatingBadge rating={statusGetByValue(item.Status)} />
        ),
        // Provide PDF export cell styling (badge background + accessible text)
        exportCellStyle: exportStyleFromValue(
          (item) => item.Status,
          (v) => statusGetByValue(v)
        ),
      },
      OutcomeLabelled: {
        custom: false,
        formId: parentType,
        fieldId: 'Outcome',
        cell: (item) => (
          <SimpleRatingBadge rating={outcomeGetByValue(item.Outcome)} />
        ),
        // Provide PDF export cell styling (badge background + accessible text)
        exportCellStyle: exportStyleFromValue(
          (item) => item.Outcome,
          (v) => outcomeGetByValue(v)
        ),
      },
      AssessedItems: {
        header: t('AssessedItems'),
      },
      TargetCompletionDate: dateColumnFromConfig({
        header: { formId: parentType, fieldId: 'TargetCompletionDate' },
        dateField: 'TargetCompletionDate',
        isDateRangeFilter: true,
      }),
      completedByUser: {
        custom: false,
        formId: parentType,
        fieldId: 'CompletedByUser',
        cell: (item) => item.completedByUser?.FriendlyName,
      },
      CreatedAtTimestamp: dateColumnFromConfig({
        header: { header: t('created_on') },
        dateField: 'CreatedAtTimestamp',
        isDateRangeFilter: true,
      }),
      ModifiedByUser: {
        header: t('updated_by_id'),
      },
      SequentialIdLabel: {
        header: t('id'),
        sortingField: 'SequentialId',
      },
      Id: {
        header: gt('guid'),
      },

      NextTestDate: dateColumnFromConfig({
        header: { formId: parentType, fieldId: 'NextTestDate' },
        dateField: 'NextTestDate',
        isDateRangeFilter: true,
      }),
      ModifiedAtTimestamp: dateColumnFromConfig({
        header: { header: t('updated_on') },
        dateField: 'ModifiedAtTimestamp',
        isDateRangeFilter: true,
      }),
      CreatedByUser: {
        header: t('created_by_id'),
      },
      allOwners,
      allContributors,
      tags: tagField,
      departments: departmentField,
    }),
    [
      allContributors,
      allOwners,
      departmentField,
      gt,
      outcomeGetByValue,
      statusGetByValue,
      t,
      tagField,
      useAbsoluteUrls,
      parentType,
    ]
  );
};

export const enum AssessmentStatus {
  Complete = 'complete',
  Inprogress = 'inprogress',
  NotStarted = 'notstarted',
}

export const getStatusByDate = (
  now: Dayjs,
  startDate?: null | string,
  completionDate?: null | string
): AssessmentStatus => {
  if (dayjs(completionDate).isBefore(now)) {
    return AssessmentStatus.Complete;
  }
  if (!startDate) {
    return AssessmentStatus.NotStarted;
  }

  return AssessmentStatus.Inprogress;
};

const useGetProps = (
  assessmentMode: AssessmentTypeEnum,
  records: AssessmentFields[] | undefined,
  useAbsoluteUrls?: boolean
): UseGetTablePropsOptions<AssessmentRegisterFields> => {
  const { registerStorageKey, taxonomyKey, parentType } =
    useAssessmentTypeConfig(assessmentMode);
  const { t: at } = useTranslation('common', { keyPrefix: taxonomyKey });
  const fields = useGetFieldConfig(assessmentMode, useAbsoluteUrls);
  const labelledFields = useLabelledFields(assessmentMode, records);

  return useMemo(
    () => ({
      customAttributeFormIds: [parentType],
      tableId: 'assessmentRegister',
      data: labelledFields,
      entityLabel: at('entity'),
      emptyCollectionAction: <></>,
      preferencesStorageKey: registerStorageKey,
      enableFiltering: true,
      usesRelativeCustomAttributeDates: true,
      initialColumns: [
        'Title',
        'StartDate',
        'ActualCompletionDate',
        'StatusLabelled',
        'allOwners',
      ],
      fields,
    }),
    [at, fields, labelledFields, registerStorageKey, parentType]
  );
};

export const useGetCollectionTableProps = (
  assessmentMode: AssessmentTypeEnum,
  records: AssessmentFields[] | undefined
): TablePropsWithActions<AssessmentRegisterFields> => {
  const props = useGetProps(assessmentMode, records);

  return useGetTableProps(props);
};

export const useGetAssessmentSmartWidgetTableProps = (
  assessmentMode: AssessmentTypeEnum,
  records: AssessmentFields[] | undefined,
  statefulTableOptions: StatefulTableOptions<AssessmentRegisterFields>
): TablePropsWithActions<AssessmentRegisterFields> => {
  const props = useGetProps(assessmentMode, records, true);

  return useGetStatelessTableProps<AssessmentRegisterFields>({
    ...props,
    ...statefulTableOptions,
    enableFiltering: false,
  });
};
